/**
 * NodeConfigDrawer — right-side panel that opens when a node is selected.
 *
 * Renders the per-type configuration form for every node type in the
 * FlowDefinition schema. Writes back via editorStore.updateNode which
 * rebuilds canvas nodes + edges.
 *
 * P-1.5 implementation: form fields for every configurable property.
 * P-1.6 implementation: visual rule builder for Condition + Switch, with a
 * "JSONLogic (advanced)" toggle to drop into raw JSON mode.
 */

import { useEffect, useMemo, useState, type ReactNode, type InputHTMLAttributes, type SelectHTMLAttributes } from "react";
import { X } from "lucide-react";
import { useEditorStore } from "./editorStore";
import type {
  ABTestNode,
  CapturePolicyNode,
  CheckoutPageNode,
  ConditionNode,
  DelayNode,
  FlowNode,
  FraudCheckNode,
  HttpRequestMethod,
  HttpRequestNode,
  ScheduleNode,
  SwitchNode,
  TriggerNode,
} from "../../types/flow";
import { getApiClient } from "../../lib/apiClient";
import { RealSyncProgress, SyntheticSyncProgress } from "../sources/SyncProgress";
import type { CostplusProject } from "../../types/api";
import { RuleBuilder } from "./RuleBuilder";
import { CountryPicker } from "./CountryPicker";
import { PAYMENT_METHODS, paymentMethodLabel } from "./data/paymentMethods";
import {
  formatDuration,
  isoToLocalInput,
  localInputToIso,
  parseDuration,
  type DurationUnit,
} from "./data/dateUtils";
import { useBuilderStore } from "../builder/builderStore";
import { useSourcesStore } from "../sources/sourcesStore";
import { ShopifyConnectModal } from "../sources/ShopifyConnectModal";
import { Plus, ExternalLink, RefreshCw } from "lucide-react";
import { Link as RouterLink, useLocation } from "react-router-dom";

export function NodeConfigDrawer() {
  const drawerNodeId = useEditorStore((s) => s.drawerNodeId);
  const definition = useEditorStore((s) => s.definition);
  const openDrawer = useEditorStore((s) => s.openDrawer);

  if (!drawerNodeId || !definition) return null;
  const node = definition.nodes[drawerNodeId];
  if (!node) return null;

  return (
    <aside className="w-96 shrink-0 border-l border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 overflow-y-auto">
      <div className="p-3 border-b border-neutral-200 dark:border-neutral-800 flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
            {titleFor(node.type)} node
          </p>
          <p className="text-xs font-mono text-neutral-500 mt-0.5">{drawerNodeId}</p>
        </div>
        <button
          onClick={() => openDrawer(null)}
          className="text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="p-3 space-y-4">
        <NodeForm nodeId={drawerNodeId} node={node} />
      </div>
    </aside>
  );
}

function titleFor(type: FlowNode["type"]): string {
  switch (type) {
    case "trigger":
      return "Trigger";
    case "condition":
      return "Condition";
    case "switch":
      return "Switch";
    case "ab_test":
      return "A/B test";
    case "schedule":
      return "Schedule";
    case "checkout_page":
      return "Checkout page";
    case "capture_policy":
      return "Capture policy";
    case "delay":
      return "Delay";
    case "fraud_check":
      return "Fraud check";
    case "http_request":
      return "HTTP request";
  }
}

function NodeForm({ nodeId, node }: { nodeId: string; node: FlowNode }) {
  return (
    <div className="space-y-4">
      <DisplayNameField nodeId={nodeId} node={node} />
      <SpecificForm nodeId={nodeId} node={node} />
      <ErrorOutputToggle nodeId={nodeId} node={node} />
    </div>
  );
}

function DisplayNameField({ nodeId, node }: { nodeId: string; node: FlowNode }) {
  const updateNode = useEditorStore((s) => s.updateNode);
  const removeNodeField = useEditorStore((s) => s.removeNodeField);
  return (
    <Field
      label="Display name"
      hint="Shown on the canvas card. Leave blank to use the default."
    >
      <TextInput
        value={node.label ?? ""}
        placeholder={titleFor(node.type)}
        onChange={(e) => {
          const v = e.target.value;
          if (v.length === 0) {
            removeNodeField(nodeId, "label");
          } else {
            updateNode(nodeId, { label: v } as Partial<FlowNode>);
          }
        }}
      />
    </Field>
  );
}

function SpecificForm({ nodeId, node }: { nodeId: string; node: FlowNode }) {
  switch (node.type) {
    case "trigger":
      return <TriggerForm nodeId={nodeId} node={node} />;
    case "condition":
      return <ConditionForm nodeId={nodeId} node={node} />;
    case "switch":
      return <SwitchForm nodeId={nodeId} node={node} />;
    case "ab_test":
      return <ABTestForm nodeId={nodeId} node={node} />;
    case "schedule":
      return <ScheduleForm nodeId={nodeId} node={node} />;
    case "checkout_page":
      return <CheckoutPageForm nodeId={nodeId} node={node} />;
    case "capture_policy":
      return <CapturePolicyForm nodeId={nodeId} node={node} />;
    case "delay":
      return <DelayForm nodeId={nodeId} node={node} />;
    case "fraud_check":
      return <FraudCheckForm nodeId={nodeId} node={node} />;
    case "http_request":
      return <HttpRequestForm nodeId={nodeId} node={node} />;
  }
}

const ERROR_OUTPUT_NODES: ReadonlySet<FlowNode["type"]> = new Set([
  "condition",
  "switch",
  "ab_test",
  "schedule",
  "delay",
  "fraud_check",
  "http_request",
]);

function ErrorOutputToggle({ nodeId, node }: { nodeId: string; node: FlowNode }) {
  const updateNode = useEditorStore((s) => s.updateNode);
  const removeNodeField = useEditorStore((s) => s.removeNodeField);
  if (!ERROR_OUTPUT_NODES.has(node.type)) return null;
  const enabled = (node as { error_next?: string }).error_next !== undefined;

  const toggle = () => {
    if (enabled) {
      removeNodeField(nodeId, "error_next");
    } else {
      updateNode(nodeId, { error_next: "" } as Partial<FlowNode>);
    }
  };

  return (
    <div className="pt-3 border-t border-neutral-200 dark:border-neutral-800">
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={enabled}
          onChange={toggle}
          className="rounded border-neutral-300 dark:border-neutral-600"
        />
        <span className="text-xs">
          <span className="font-medium">Enable error output</span>
          <span className="block text-[11px] text-neutral-500">
            Adds an extra output that fires when this node fails (timeout, exception, non-2xx). Leave off if you don't need to handle errors.
          </span>
        </span>
      </label>
    </div>
  );
}

// ─── Shared primitives ───────────────────────────────────────────────────────

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-neutral-700 dark:text-neutral-300">
        {label}
      </span>
      {hint ? (
        <span className="block text-[11px] text-neutral-500 mt-0.5">{hint}</span>
      ) : null}
      <div className="mt-1">{children}</div>
    </label>
  );
}

function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className="w-full rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300 dark:focus:ring-neutral-600"
    />
  );
}

function SelectInput(
  props: SelectHTMLAttributes<HTMLSelectElement> & {
    options: { value: string; label: string }[];
  },
) {
  const { options, ...rest } = props;
  return (
    <select
      {...rest}
      className="w-full rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-2.5 py-1.5 text-sm"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

// ─── Trigger ─────────────────────────────────────────────────────────────────

function TriggerForm({ nodeId, node }: { nodeId: string; node: TriggerNode }) {
  const updateNode = useEditorStore((s) => s.updateNode);
  const flowId = useEditorStore((s) => s.flowId);
  const refreshSources = useSourcesStore((s) => s.refresh);
  const stores = useSourcesStore((s) => s.shopifyStores);
  const [connecting, setConnecting] = useState(false);
  const location = useLocation();

  // Make sure the connected-stores list is fresh whenever the user opens
  // a trigger drawer; covers the case where they just installed a store
  // on /sources and now want to bind it.
  useEffect(() => {
    void refreshSources();
  }, [refreshSources]);

  return (
    <div className="space-y-3">
      <Field
        label="Source"
        hint="Bind one source to this trigger. Shopify sources must be connected on the Sources page first."
      >
        <div className="space-y-2">
          {node.sources.map((src, idx) => (
            <div
              key={idx}
              className="rounded-md border border-neutral-200 dark:border-neutral-700 px-2 py-1.5 space-y-2"
            >
              <div className="flex items-center gap-2">
                <SelectInput
                  value={src.type}
                  onChange={(e) => {
                    const sources = [...node.sources];
                    const newType = e.target.value as TriggerNode["sources"][number]["type"];
                    // Reset reference when switching types — a Shopify shop domain
                    // is meaningless for a custom/api source.
                    sources[idx] = { type: newType };
                    updateNode(nodeId, { sources });
                  }}
                  options={[
                    { value: "shopify", label: "Shopify" },
                    { value: "api", label: "API" },
                    { value: "email", label: "Email" },
                    { value: "payment_link", label: "Payment link" },
                    { value: "custom", label: "Custom" },
                  ]}
                />
                {src.type === "shopify" ? (
                  <>
                    <ShopifyStorePicker
                      value={src.reference ?? ""}
                      currentFlowId={flowId}
                      stores={stores}
                      onChange={(ref) => {
                        const sources = [...node.sources];
                        sources[idx] = ref ? { type: "shopify", reference: ref } : { type: "shopify" };
                        updateNode(nodeId, { sources });
                      }}
                    />
                    {src.reference ? <ShopifySyncButton shopDomain={src.reference} /> : null}
                  </>
                ) : (
                  <TextInput
                    placeholder="reference (optional)"
                    value={src.reference ?? ""}
                    onChange={(e) => {
                      const sources = [...node.sources];
                      const value = e.target.value;
                      sources[idx] = value
                        ? { ...sources[idx], reference: value }
                        : { type: sources[idx].type };
                      updateNode(nodeId, { sources });
                    }}
                  />
                )}
                {/* Remove only renders for legacy flows that carry more than
                    one source — new flows always have exactly one. Hides on
                    the last source so the trigger can't be left sourceless
                    (the validator would reject it on publish anyway). */}
                {node.sources.length > 1 ? (
                  <button
                    className="text-neutral-500 hover:text-category-error"
                    onClick={() => {
                      const sources = node.sources.filter((_, i) => i !== idx);
                      updateNode(nodeId, { sources });
                    }}
                    aria-label="Remove source"
                  >
                    <X className="w-4 h-4" />
                  </button>
                ) : null}
              </div>
              {src.type === "shopify" && src.reference ? (
                <ShopifyBindingNote shop={src.reference} stores={stores} currentFlowId={flowId} />
              ) : null}
            </div>
          ))}
          <div className="pt-1 border-t border-neutral-200 dark:border-neutral-800 flex items-center justify-between text-[11px] text-neutral-500">
            <span>Need a new Shopify store?</span>
            <button
              onClick={() => setConnecting(true)}
              className="inline-flex items-center gap-1 rounded-md border border-neutral-200 dark:border-neutral-700 px-2 py-1 font-medium hover:bg-neutral-50 dark:hover:bg-neutral-900"
            >
              <Plus className="w-3 h-3" />
              Connect Shopify store
            </button>
          </div>
        </div>
      </Field>
      <ShopifyConnectModal
        open={connecting}
        onClose={() => setConnecting(false)}
        // Bring the merchant back to this flow's editor (with the trigger
        // drawer open) after the Shopify round-trip.
        returnTo={flowId ? `${location.pathname}?shopify_connected=1` : undefined}
      />
    </div>
  );
}

// ShopifySyncButton re-syncs the bound Shopify store's shipping zones,
// automatic discounts, and product catalog from Shopify's Admin API.
// Useful when merchants edit these in Shopify admin and want the
// checkout to reflect them immediately (without waiting for the daily
// ticker or shop/update webhook). Renders an inline progress bar while
// the sync runs.
function ShopifySyncButton({ shopDomain }: { shopDomain: string }) {
  const [syncing, setSyncing] = useState(false);
  if (syncing) {
    return (
      <div className="shrink-0 min-w-[180px]">
        <RealSyncProgress
          shopDomain={shopDomain}
          onComplete={() => setSyncing(false)}
        />
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await getApiClient().shopify.resyncStore(shopDomain);
          setSyncing(true);
        } catch (err) {
          alert(err instanceof Error ? err.message : "Sync failed.");
        }
      }}
      className="shrink-0 rounded-md border border-neutral-200 dark:border-neutral-700 px-2 py-1.5 text-xs hover:bg-neutral-50 dark:hover:bg-neutral-900 inline-flex items-center gap-1"
      title="Re-sync this Shopify store's shipping / discount / product settings"
    >
      <RefreshCw className="w-3 h-3" />
      Sync
    </button>
  );
}

function ShopifyStorePicker({
  value,
  currentFlowId,
  stores,
  onChange,
}: {
  value: string;
  currentFlowId: string | null;
  stores: { shop_domain: string; active: boolean; bound_to?: { flow_id: string } }[];
  onChange: (reference: string) => void;
}) {
  // Build options, marking each store with a status the dropdown can render.
  const isBlocked = (shop: { shop_domain: string; active: boolean; bound_to?: { flow_id: string } }) => {
    if (!shop.active) return true;
    if (shop.bound_to && shop.bound_to.flow_id !== currentFlowId) return true;
    return false;
  };
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="flex-1 min-w-0 rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-2.5 py-1.5 text-sm font-mono"
    >
      <option value="">— pick a connected store —</option>
      {stores.length === 0 ? (
        <option value="" disabled>
          No stores connected. Connect one from the Sources page.
        </option>
      ) : null}
      {stores.map((s) => {
        const blocked = isBlocked(s);
        const suffix = !s.active
          ? "  ⊘ uninstalled"
          : s.bound_to && s.bound_to.flow_id !== currentFlowId
            ? "  ⊘ already used"
            : "";
        return (
          <option key={s.shop_domain} value={s.shop_domain} disabled={blocked && s.shop_domain !== value}>
            {s.shop_domain}
            {suffix}
          </option>
        );
      })}
    </select>
  );
}

function ShopifyBindingNote({
  shop,
  stores,
  currentFlowId,
}: {
  shop: string;
  stores: { shop_domain: string; bound_to?: { flow_id: string; flow_name: string } }[];
  currentFlowId: string | null;
}) {
  const match = stores.find((s) => s.shop_domain === shop);
  if (!match || !match.bound_to) return null;
  if (match.bound_to.flow_id === currentFlowId) return null;
  return (
    <p className="text-[11px] text-category-error inline-flex items-center gap-1">
      Already used by{" "}
      <RouterLink
        to={`/flows/${match.bound_to.flow_id}`}
        className="underline decoration-dotted underline-offset-2 inline-flex items-center gap-0.5"
      >
        "{match.bound_to.flow_name}"
        <ExternalLink className="w-3 h-3" />
      </RouterLink>
      . Remove it there before binding here.
    </p>
  );
}

// ─── Condition ───────────────────────────────────────────────────────────────

function ConditionForm({ nodeId, node }: { nodeId: string; node: ConditionNode }) {
  const updateNode = useEditorStore((s) => s.updateNode);
  return (
    <div className="space-y-3">
      <Field label="Rule" hint="Evaluates to true or false. Routes to true_next or false_next.">
        <RuleBuilder
          value={node.rule}
          onChange={(rule) => updateNode(nodeId, { rule })}
        />
      </Field>
    </div>
  );
}

// ─── Switch ──────────────────────────────────────────────────────────────────

function SwitchForm({ nodeId, node }: { nodeId: string; node: SwitchNode }) {
  const updateNode = useEditorStore((s) => s.updateNode);
  return (
    <div className="space-y-3">
      <Field label="Expression" hint="Evaluates to a value; matched against branch values.">
        <RuleBuilder
          value={node.expr}
          onChange={(expr) => updateNode(nodeId, { expr })}
        />
      </Field>
      <Field label="Branches">
        <div className="space-y-2">
          {node.branches.map((b, idx) => (
            <div
              key={idx}
              className="flex items-center gap-2 rounded-md border border-neutral-200 dark:border-neutral-700 px-2 py-1.5"
            >
              <TextInput
                placeholder="value (string/number/bool)"
                value={String(b.value)}
                onChange={(e) => {
                  const branches = [...node.branches];
                  branches[idx] = { ...branches[idx], value: e.target.value };
                  updateNode(nodeId, { branches });
                }}
              />
              <button
                className="text-neutral-500 hover:text-category-error"
                onClick={() =>
                  updateNode(nodeId, {
                    branches: node.branches.filter((_, i) => i !== idx),
                  })
                }
                aria-label="Remove branch"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button
            onClick={() =>
              updateNode(nodeId, {
                branches: [...node.branches, { value: "", next: "" }],
              })
            }
            className="text-xs font-medium text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100"
          >
            + Add branch
          </button>
        </div>
      </Field>
    </div>
  );
}

// ─── A/B test ────────────────────────────────────────────────────────────────

function ABTestForm({ nodeId, node }: { nodeId: string; node: ABTestNode }) {
  const updateNode = useEditorStore((s) => s.updateNode);
  const totalWeight = node.variants.reduce((acc, v) => acc + v.weight, 0);
  const weightsValid = totalWeight === 100;

  const normalize = () => {
    if (node.variants.length === 0) return;
    if (totalWeight <= 0) {
      // All zero — split evenly.
      const share = Math.floor(100 / node.variants.length);
      const remainder = 100 - share * node.variants.length;
      const variants = node.variants.map((v, i) => ({
        ...v,
        weight: share + (i === 0 ? remainder : 0),
      }));
      updateNode(nodeId, { variants });
      return;
    }
    // Scale to sum to 100, keep integer weights, push rounding delta onto the
    // first variant so the total is always exactly 100.
    const scaled = node.variants.map((v) => (v.weight / totalWeight) * 100);
    const rounded = scaled.map((w) => Math.floor(w));
    const delta = 100 - rounded.reduce((a, b) => a + b, 0);
    const variants = node.variants.map((v, i) => ({
      ...v,
      weight: rounded[i] + (i === 0 ? delta : 0),
    }));
    updateNode(nodeId, { variants });
  };

  return (
    <div className="space-y-3">
      <Field label="Test key" hint="Used to hash session ID for stickiness.">
        <TextInput
          value={node.test_key}
          onChange={(e) => updateNode(nodeId, { test_key: e.target.value })}
        />
      </Field>
      <Field
        label="Variants"
        hint="Weights are percentages. Every variant capped at 100; all variants must sum to exactly 100."
      >
        <div className="space-y-2">
          {node.variants.map((v, idx) => (
            <div
              key={idx}
              className="flex items-center gap-2 rounded-md border border-neutral-200 dark:border-neutral-700 px-2 py-1.5"
            >
              <TextInput
                placeholder="id"
                value={v.id}
                onChange={(e) => {
                  const variants = [...node.variants];
                  variants[idx] = { ...variants[idx], id: e.target.value };
                  updateNode(nodeId, { variants });
                }}
                style={{ width: 80 }}
              />
              <div className="relative" style={{ width: 90 }}>
                <TextInput
                  placeholder="weight"
                  type="number"
                  min={0}
                  max={100}
                  value={v.weight}
                  onChange={(e) => {
                    const raw = Number.parseInt(e.target.value, 10);
                    const clamped = Number.isNaN(raw)
                      ? 0
                      : Math.max(0, Math.min(100, raw));
                    const variants = [...node.variants];
                    variants[idx] = { ...variants[idx], weight: clamped };
                    updateNode(nodeId, { variants });
                  }}
                  style={{ paddingRight: 18 }}
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-neutral-500 pointer-events-none">
                  %
                </span>
              </div>
              <button
                className="text-neutral-500 hover:text-category-error ml-auto"
                onClick={() =>
                  updateNode(nodeId, {
                    variants: node.variants.filter((_, i) => i !== idx),
                  })
                }
                aria-label="Remove variant"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
          <div className="flex items-center justify-between text-[11px]">
            <button
              onClick={() =>
                updateNode(nodeId, {
                  variants: [
                    ...node.variants,
                    { id: `V${node.variants.length + 1}`, weight: 0, next: "" },
                  ],
                })
              }
              className="font-medium text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100"
            >
              + Add variant
            </button>
            <div className="flex items-center gap-2">
              <span
                className={
                  weightsValid
                    ? "text-category-trigger"
                    : "text-category-error"
                }
              >
                Total: {totalWeight}%
              </span>
              {!weightsValid ? (
                <button
                  onClick={normalize}
                  className="rounded border border-neutral-300 dark:border-neutral-700 px-1.5 py-0.5 hover:bg-neutral-50 dark:hover:bg-neutral-900"
                >
                  Normalize to 100
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </Field>
    </div>
  );
}

// ─── Schedule ────────────────────────────────────────────────────────────────

function ScheduleForm({ nodeId, node }: { nodeId: string; node: ScheduleNode }) {
  const updateNode = useEditorStore((s) => s.updateNode);
  const dayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const selectedDays = node.window.days_of_week ?? [];

  const toggleDay = (d: number) => {
    const next = selectedDays.includes(d)
      ? selectedDays.filter((x) => x !== d)
      : [...selectedDays, d].sort((a, b) => a - b);
    updateNode(nodeId, {
      window: { ...node.window, days_of_week: next.length > 0 ? next : undefined },
    });
  };

  return (
    <div className="space-y-3">
      <Field label="Active from">
        <input
          type="datetime-local"
          value={isoToLocalInput(node.window.start)}
          onChange={(e) =>
            updateNode(nodeId, {
              window: { ...node.window, start: localInputToIso(e.target.value) },
            })
          }
          className="w-full rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-2.5 py-1.5 text-sm"
        />
      </Field>
      <Field label="Active until">
        <input
          type="datetime-local"
          value={isoToLocalInput(node.window.end)}
          onChange={(e) =>
            updateNode(nodeId, {
              window: { ...node.window, end: localInputToIso(e.target.value) },
            })
          }
          className="w-full rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-2.5 py-1.5 text-sm"
        />
      </Field>
      <Field label="Timezone (optional)" hint="IANA name. Defaults to the merchant's locale.">
        <TextInput
          placeholder="Europe/Paris"
          value={node.window.timezone ?? ""}
          onChange={(e) =>
            updateNode(nodeId, {
              window: { ...node.window, timezone: e.target.value || undefined },
            })
          }
        />
      </Field>
      <Field label="Days of week (optional)" hint="Empty = every day.">
        <div className="flex gap-1 flex-wrap">
          {dayLabels.map((label, d) => {
            const on = selectedDays.includes(d);
            return (
              <button
                key={d}
                type="button"
                onClick={() => toggleDay(d)}
                className={
                  "px-2.5 py-1 rounded-md border text-xs font-medium transition-colors " +
                  (on
                    ? "bg-neutral-900 dark:bg-neutral-100 text-white dark:text-neutral-900 border-neutral-900 dark:border-neutral-100"
                    : "bg-white dark:bg-neutral-950 text-neutral-600 dark:text-neutral-400 border-neutral-200 dark:border-neutral-700 hover:border-neutral-400")
                }
              >
                {label}
              </button>
            );
          })}
        </div>
      </Field>
    </div>
  );
}

// ─── Checkout page ───────────────────────────────────────────────────────────

function CheckoutPageForm({ nodeId, node }: { nodeId: string; node: CheckoutPageNode }) {
  const updateNode = useEditorStore((s) => s.updateNode);
  const definition = useEditorStore((s) => s.definition);
  const flowId = useEditorStore((s) => s.flowId);
  const [projects, setProjects] = useState<CostplusProject[]>([]);

  useEffect(() => {
    let cancelled = false;
    getApiClient()
      .costplusProjects.list()
      .then((ps) => {
        if (!cancelled) setProjects(ps);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Find the first Shopify source on the flow's start/trigger node, if any.
  // Used to show the "Sync from Shopify" button only when it can actually
  // resolve to a shop.
  const shopifyShopDomain = useMemo<string | null>(() => {
    if (!definition) return null;
    const start = definition.nodes[definition.start_node];
    if (!start || start.type !== "trigger") return null;
    const shopify = start.sources.find((s) => s.type === "shopify" && s.reference);
    return shopify?.reference ?? null;
  }, [definition]);

  const [syncing, setSyncing] = useState(false);
  const [syncErr, setSyncErr] = useState<string | null>(null);

  const syncFromShopify = async () => {
    if (!shopifyShopDomain || !flowId || syncing) return;
    setSyncing(true);
    setSyncErr(null);
    try {
      const result = await getApiClient().flows.syncFromSource(flowId, {
        source_type: "shopify",
        source_reference: shopifyShopDomain,
      });
      // Apply every field the backend returned. `live_rates: false` on
      // synced methods because Shopify's stored method definitions are
      // static at sync time — the runtime calc-rate-on-address path
      // pulls live rates separately via the adapter.
      const patch: Partial<CheckoutPageNode> = {
        allowed_countries: result.allowed_countries ?? [],
        shipping_methods: (result.shipping_methods ?? []).map((m) => ({
          id: m.id,
          name: m.name,
          countries: m.countries ?? [],
          price_cents: m.price_cents ?? 0,
          currency: m.currency ?? "",
          live_rates: false,
        })),
        sync_metadata: {
          source_type: "shopify",
          source_reference: shopifyShopDomain,
          synced_at: new Date().toISOString(),
          synced_by: "studio",
        },
      };
      if (result.default_locale) patch.default_locale = result.default_locale;
      if (result.tax_display) patch.tax_display = result.tax_display;
      updateNode(nodeId, patch);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Sync failed";
      setSyncErr(msg);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-3">
      <Field
        label="Project"
        hint="Sourced from your linked Cost+ account. Determines acquirer + compliance + API key for order creation."
      >
        <SelectInput
          value={node.project_id}
          onChange={(e) => updateNode(nodeId, { project_id: e.target.value })}
          options={[
            { value: "", label: "— select a project —" },
            ...projects.map((p) => ({
              value: p.id,
              label: `${p.name} (${p.region})`,
            })),
          ]}
        />
      </Field>
      <Field label="Theme">
        <TextInput
          value={node.theme_id}
          onChange={(e) => updateNode(nodeId, { theme_id: e.target.value })}
        />
      </Field>
      <Field label="Layout">
        <SelectInput
          value={node.layout}
          onChange={(e) =>
            updateNode(nodeId, {
              layout: e.target.value as CheckoutPageNode["layout"],
            })
          }
          options={[
            { value: "single_page", label: "Single page (Shopify-style)" },
            { value: "multi_step", label: "Multi-step (3-step wizard)" },
          ]}
        />
      </Field>
      <Field
        label="Payment methods"
        hint="Buyer sees the methods you enable, in the order you select them. Express methods (Apple/Google Pay) render in the express row above the form."
      >
        <PaymentMethodPicker
          value={node.payment_methods}
          onChange={(methods) => updateNode(nodeId, { payment_methods: methods })}
        />
      </Field>
      <Field
        label="Allowed countries"
        hint={
          node.sync_metadata
            ? `Synced from ${node.sync_metadata.source_type}:${node.sync_metadata.source_reference} at ${new Date(node.sync_metadata.synced_at).toLocaleString()}`
            : "Empty means no geographic restriction."
        }
      >
        <div className="space-y-1.5">
          <CountryPicker
            value={node.allowed_countries}
            onChange={(codes) => updateNode(nodeId, { allowed_countries: codes })}
          />
          {shopifyShopDomain ? (
            <>
              <button
                type="button"
                onClick={syncFromShopify}
                disabled={syncing}
                className="w-full rounded-md border border-neutral-200 dark:border-neutral-700 px-2.5 py-1.5 text-xs font-medium hover:bg-neutral-50 dark:hover:bg-neutral-900 disabled:opacity-60 disabled:cursor-not-allowed"
                title={`Pull ship-to countries + shipping methods from ${shopifyShopDomain}`}
              >
                {syncing
                  ? "Syncing…"
                  : `Sync from Shopify (${shopifyShopDomain})`}
              </button>
              {syncing || syncErr ? (
                <SyntheticSyncProgress
                  done={!syncing}
                  errored={!!syncErr}
                  expectedDurationMs={1500}
                  label="Reading shop config + shipping zones…"
                />
              ) : null}
              {syncErr ? (
                <p className="text-[11px] text-category-error">{syncErr}</p>
              ) : null}
            </>
          ) : null}
        </div>
      </Field>
      <Field label="Tax display">
        <SelectInput
          value={node.tax_display ?? "exclusive"}
          onChange={(e) =>
            updateNode(nodeId, { tax_display: e.target.value as "inclusive" | "exclusive" })
          }
          options={[
            { value: "inclusive", label: "Inclusive" },
            { value: "exclusive", label: "Exclusive" },
          ]}
        />
      </Field>
      <Field
        label="Pay button color"
        hint="Overrides the theme's primary CTA color for this checkout. Click the swatch to pick."
      >
        <ColorOverrideRow
          value={node.theme_overrides?.pay_button_color ?? ""}
          fallback="#ef4123"
          onChange={(color) => {
            const overrides = { ...(node.theme_overrides ?? {}) };
            if (color) {
              overrides.pay_button_color = color;
            } else {
              delete overrides.pay_button_color;
            }
            const isEmpty = Object.keys(overrides).length === 0;
            updateNode(nodeId, {
              theme_overrides: isEmpty ? undefined : overrides,
            });
          }}
        />
      </Field>
      <Field
        label="Pay button text color"
        hint="Defaults to white. Pick a darker color if your button background is light."
      >
        <ColorOverrideRow
          value={node.theme_overrides?.pay_button_text_color ?? ""}
          fallback="#ffffff"
          onChange={(color) => {
            const overrides = { ...(node.theme_overrides ?? {}) };
            if (color) {
              overrides.pay_button_text_color = color;
            } else {
              delete overrides.pay_button_text_color;
            }
            const isEmpty = Object.keys(overrides).length === 0;
            updateNode(nodeId, {
              theme_overrides: isEmpty ? undefined : overrides,
            });
          }}
        />
      </Field>
      <div className="pt-2 border-t border-neutral-200 dark:border-neutral-800 space-y-2">
        <p className="text-[11px] text-neutral-500 italic">
          Layout blocks (page builder) opens via double-click on this node.
        </p>
        <OpenPageBuilderButton nodeId={nodeId} />
        <OpenBuyerPreviewLink nodeId={nodeId} />
      </div>
    </div>
  );
}

function OpenPageBuilderButton({ nodeId }: { nodeId: string }) {
  const openFor = useBuilderStore((s) => s.openFor);
  return (
    <button
      onClick={() => openFor(nodeId)}
      className="w-full rounded-md border border-neutral-200 dark:border-neutral-700 px-2.5 py-1.5 text-xs font-medium hover:bg-neutral-50 dark:hover:bg-neutral-900"
    >
      Open page builder
    </button>
  );
}

function OpenBuyerPreviewLink({ nodeId }: { nodeId: string }) {
  const flowId = useEditorStore((s) => s.flowId);
  if (!flowId) return null;
  return (
    <a
      href={`/preview/${flowId}/${nodeId}`}
      target="_blank"
      rel="noopener noreferrer"
      className="block text-center w-full rounded-md border border-neutral-200 dark:border-neutral-700 px-2.5 py-1.5 text-xs font-medium hover:bg-neutral-50 dark:hover:bg-neutral-900"
    >
      Open buyer preview ↗
    </a>
  );
}

// ─── Capture policy ──────────────────────────────────────────────────────────

function CapturePolicyForm({ nodeId, node }: { nodeId: string; node: CapturePolicyNode }) {
  const updateNode = useEditorStore((s) => s.updateNode);
  return (
    <Field
      label="Capture mode"
      hint="auto = immediate capture; manual_on_fulfillment = Shopify fulfillments/create triggers capture; manual_explicit = merchant action required."
    >
      <SelectInput
        value={node.mode}
        onChange={(e) =>
          updateNode(nodeId, { mode: e.target.value as CapturePolicyNode["mode"] })
        }
        options={[
          { value: "auto", label: "auto" },
          { value: "manual_on_fulfillment", label: "manual_on_fulfillment" },
          { value: "manual_explicit", label: "manual_explicit" },
        ]}
      />
    </Field>
  );
}

// ─── Delay ───────────────────────────────────────────────────────────────────

function DelayForm({ nodeId, node }: { nodeId: string; node: DelayNode }) {
  const updateNode = useEditorStore((s) => s.updateNode);
  const parsed =
    node.duration.mode === "fixed" ? parseDuration(node.duration.iso8601) : null;
  const fixedAmount = parsed?.amount ?? 24;
  const fixedUnit: DurationUnit = parsed?.unit ?? "hours";
  const untilIso =
    node.duration.mode === "until" && typeof node.duration.expr === "string"
      ? node.duration.expr
      : "";

  return (
    <div className="space-y-3">
      <Field label="Wait">
        <SelectInput
          value={node.duration.mode}
          onChange={(e) => {
            const mode = e.target.value as "fixed" | "until";
            updateNode(nodeId, {
              duration:
                mode === "fixed"
                  ? { mode: "fixed", iso8601: "PT24H" }
                  : { mode: "until", expr: new Date(Date.now() + 24 * 3600 * 1000).toISOString() },
            });
          }}
          options={[
            { value: "fixed", label: "A fixed amount of time" },
            { value: "until", label: "Until a specific date / time" },
          ]}
        />
      </Field>
      {node.duration.mode === "fixed" ? (
        <Field label="Duration" hint="Maximum 30 days.">
          <div className="flex items-center gap-2">
            <TextInput
              type="number"
              min={1}
              value={fixedAmount}
              onChange={(e) => {
                const amount = Math.max(1, Number.parseInt(e.target.value, 10) || 1);
                updateNode(nodeId, {
                  duration: { mode: "fixed", iso8601: formatDuration({ amount, unit: fixedUnit }) },
                });
              }}
              style={{ width: 100 }}
            />
            <SelectInput
              value={fixedUnit}
              onChange={(e) => {
                const unit = e.target.value as DurationUnit;
                updateNode(nodeId, {
                  duration: { mode: "fixed", iso8601: formatDuration({ amount: fixedAmount, unit }) },
                });
              }}
              options={[
                { value: "minutes", label: "Minutes" },
                { value: "hours", label: "Hours" },
                { value: "days", label: "Days" },
              ]}
            />
          </div>
        </Field>
      ) : (
        <Field label="Resume at" hint="The flow waits until the chosen date and time.">
          <input
            type="datetime-local"
            value={isoToLocalInput(untilIso)}
            onChange={(e) =>
              updateNode(nodeId, {
                duration: { mode: "until", expr: localInputToIso(e.target.value) },
              })
            }
            className="w-full rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-2.5 py-1.5 text-sm"
          />
        </Field>
      )}
    </div>
  );
}

// ─── Fraud check ─────────────────────────────────────────────────────────────

function FraudCheckForm({ nodeId, node }: { nodeId: string; node: FraudCheckNode }) {
  const updateNode = useEditorStore((s) => s.updateNode);
  const providers = useMemo(
    () => [
      { value: "cost_plus", label: "Cost+ built-in" },
      { value: "shopify", label: "Shopify fraud_risk (post-checkout only)" },
      { value: "sift", label: "Sift (BYO credentials)" },
      { value: "signifyd", label: "Signifyd (BYO credentials)" },
    ],
    [],
  );
  return (
    <Field label="Provider">
      <SelectInput
        value={node.provider}
        onChange={(e) =>
          updateNode(nodeId, { provider: e.target.value as FraudCheckNode["provider"] })
        }
        options={providers}
      />
    </Field>
  );
}

// ─── HTTP request ────────────────────────────────────────────────────────────

function HttpRequestForm({ nodeId, node }: { nodeId: string; node: HttpRequestNode }) {
  const updateNode = useEditorStore((s) => s.updateNode);
  const headers = node.headers ?? [];

  return (
    <div className="space-y-3">
      <Field label="Method">
        <SelectInput
          value={node.method}
          onChange={(e) =>
            updateNode(nodeId, { method: e.target.value as HttpRequestMethod })
          }
          options={[
            { value: "GET", label: "GET" },
            { value: "POST", label: "POST" },
            { value: "PUT", label: "PUT" },
            { value: "PATCH", label: "PATCH" },
            { value: "DELETE", label: "DELETE" },
          ]}
        />
      </Field>
      <Field
        label="URL"
        hint="Full URL including https://. {{var}} placeholders are interpolated against the session context at runtime."
      >
        <TextInput
          placeholder="https://your-app.example.com/webhook"
          value={node.url}
          onChange={(e) => updateNode(nodeId, { url: e.target.value })}
        />
      </Field>
      <Field label="Headers" hint="Common: Content-Type, Authorization, X-API-Key">
        <div className="space-y-2">
          {headers.map((h, idx) => (
            <div
              key={idx}
              className="flex items-center gap-2 rounded-md border border-neutral-200 dark:border-neutral-700 px-2 py-1.5"
            >
              <TextInput
                placeholder="Key"
                value={h.key}
                onChange={(e) => {
                  const next = [...headers];
                  next[idx] = { ...next[idx], key: e.target.value };
                  updateNode(nodeId, { headers: next });
                }}
                style={{ width: 120 }}
              />
              <TextInput
                placeholder="Value"
                value={h.value}
                onChange={(e) => {
                  const next = [...headers];
                  next[idx] = { ...next[idx], value: e.target.value };
                  updateNode(nodeId, { headers: next });
                }}
              />
              <button
                className="text-neutral-500 hover:text-category-error"
                onClick={() =>
                  updateNode(nodeId, {
                    headers: headers.filter((_, i) => i !== idx),
                  })
                }
                aria-label="Remove header"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button
            onClick={() =>
              updateNode(nodeId, {
                headers: [...headers, { key: "", value: "" }],
              })
            }
            className="text-xs font-medium text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100"
          >
            + Add header
          </button>
        </div>
      </Field>
      {node.method !== "GET" && node.method !== "DELETE" ? (
        <Field
          label="Body"
          hint="JSON or any string. {{var}} placeholders are interpolated; otherwise sent verbatim."
        >
          <textarea
            value={node.body ?? ""}
            onChange={(e) => updateNode(nodeId, { body: e.target.value })}
            rows={6}
            spellCheck={false}
            placeholder={'{"session_id": "{{session.id}}", "amount_cents": {{cart.total}}}'}
            className="w-full rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-2 py-1.5 text-xs font-mono"
          />
        </Field>
      ) : null}
      <Field label="Timeout (ms)" hint="Default 10000. Max 30000.">
        <TextInput
          type="number"
          min={500}
          max={30000}
          value={node.timeout_ms ?? 10000}
          onChange={(e) => {
            const raw = Number.parseInt(e.target.value, 10);
            const clamped = Number.isNaN(raw) ? 10000 : Math.max(500, Math.min(30000, raw));
            updateNode(nodeId, { timeout_ms: clamped });
          }}
        />
      </Field>
    </div>
  );
}

// ─── Payment method picker ───────────────────────────────────────────────────

function PaymentMethodPicker({
  value,
  onChange,
}: {
  value: string[];
  onChange: (methods: string[]) => void;
}) {
  const selected = new Set(value);

  const toggle = (id: string) => {
    if (selected.has(id)) {
      onChange(value.filter((v) => v !== id));
    } else {
      onChange([...value, id]);
    }
  };

  return (
    <div className="space-y-2">
      <ul className="space-y-1.5">
        {PAYMENT_METHODS.map((m) => {
          const checked = selected.has(m.id);
          return (
            <li key={m.id}>
              <label
                className={
                  "flex items-start gap-2 rounded-md border px-2.5 py-1.5 cursor-pointer " +
                  (checked
                    ? "border-neutral-900 dark:border-neutral-100 bg-neutral-50 dark:bg-neutral-900"
                    : "border-neutral-200 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-900")
                }
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(m.id)}
                  className="mt-0.5 rounded border-neutral-300 dark:border-neutral-600"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-xs font-medium">{m.label}</span>
                    {m.express ? (
                      <span className="text-[10px] uppercase tracking-wide px-1 py-0.5 rounded bg-category-action/10 text-category-action">
                        express
                      </span>
                    ) : null}
                    <span className="text-[10px] text-neutral-500">
                      {m.regions.join(", ")}
                    </span>
                  </div>
                  {m.hint ? (
                    <p className="text-[11px] text-neutral-500 mt-0.5">{m.hint}</p>
                  ) : null}
                </div>
              </label>
            </li>
          );
        })}
      </ul>
      {value.length > 0 ? (
        <p className="text-[11px] text-neutral-500">
          Order: {value.map((id) => paymentMethodLabel(id)).join(" → ")}
        </p>
      ) : (
        <p className="text-[11px] text-category-error">
          Pick at least one payment method.
        </p>
      )}
    </div>
  );
}

// ─── Color override row ─────────────────────────────────────────────────────

function ColorOverrideRow({
  value,
  fallback,
  onChange,
}: {
  value: string;
  fallback: string;
  onChange: (color: string) => void;
}) {
  // The native color input always returns a value, even when empty. We track
  // "user didn't set anything" via an empty string passed in from the parent
  // and render a "Reset" button to clear back to the theme default.
  const effective = value || fallback;
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={effective}
        onChange={(e) => onChange(e.target.value)}
        className="w-10 h-9 rounded border border-neutral-200 dark:border-neutral-700 cursor-pointer p-0.5"
        aria-label="Pick a color"
      />
      <input
        type="text"
        value={value}
        onChange={(e) => {
          const v = e.target.value.trim();
          // Accept #rgb / #rrggbb; ignore other input until valid.
          if (v === "" || /^#[0-9a-fA-F]{3,8}$/.test(v)) onChange(v);
        }}
        placeholder={`Default: ${fallback}`}
        className="flex-1 rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-2.5 py-1.5 text-sm font-mono"
      />
      {value ? (
        <button
          type="button"
          onClick={() => onChange("")}
          className="text-xs text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100 underline"
        >
          Reset
        </button>
      ) : null}
    </div>
  );
}
